import React, { useRef, useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { criarPost, PostInput } from '../services/postService';
import { Torneio } from '../services/torneioService';
import { IdentidadeFeed } from '../types/identidadeFeed';
import { escolherIdentidadePadrao, resolverIdsParaMutacao } from '../utils/identidadesFeed';
import { CurrentProfile } from '../types/profile';
import IdentidadeFeedSelector from './IdentidadeFeedSelector';

interface UsuarioResumo {
    nome?: string;
    username?: string;
    imagemUrl?: string;
}

interface Atleta {
    id: string;
    usuario?: UsuarioResumo;
}

export interface AutorLiga {
    id: string;
    nome: string;
    imagemUrl?: string | null;
}

export interface CreatepostProps {
    autorAtleta?: Atleta;
    autorTorneio?: Torneio;
    autorLiga?: AutorLiga;
    ligaId?: string;
    identidades?: IdentidadeFeed[];
    /**
     * Perfil ativo (`ProfileContext.currentProfile`), derivado pela page e
     * passado por prop — o componente não consome o contexto por conta própria
     * (`ai/anti-patterns.md` §1). Define qual identidade nasce pré-selecionada.
     */
    perfilAtivo?: CurrentProfile | null;
    onPostCreated?: () => void;
}

const Createpost: React.FC<CreatepostProps> = ({ autorAtleta, autorTorneio, autorLiga, ligaId, identidades, perfilAtivo, onPostCreated }) => {
    const usarIdentidades = !!identidades && identidades.length > 0;

    const [isOpen, setIsOpen] = useState(false);
    const [texto, setTexto] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    // Novo sistema: identidades. A identidade padrão vem do perfil ativo; o
    // primeiro item da lista é só o fallback de quem não informa perfil.
    const defaultIdentidade = escolherIdentidadePadrao(identidades, perfilAtivo);
    const [identidadeSelecionada, setIdentidadeSelecionada] = useState<IdentidadeFeed | null>(defaultIdentidade);

    // Legacy: seletor torneio/liga
    const [selectedAutor, setSelectedAutor] = useState<'torneio' | 'liga'>(autorLiga ? 'liga' : 'torneio');

    // Legacy computed values
    const hasMultipleIdentities = !usarIdentidades && !!(autorTorneio && autorLiga);
    const isPostingAsLiga = usarIdentidades
        ? identidadeSelecionada?.tipo === 'LIGA'
        : (!!ligaId || (!!autorLiga && (!hasMultipleIdentities || selectedAutor === 'liga')));
    const canPublish = usarIdentidades
        ? !!identidadeSelecionada
        : !!(autorAtleta || autorTorneio || autorLiga || ligaId);

    useEffect(() => {
        const urls = files.map((file) => URL.createObjectURL(file));
        setPreviewUrls(urls);

        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [files]);

    const menuClass = isOpen ? 'show' : '';

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    };

    const handlePublish = async () => {
        if (!canPublish) {
            setError('Autor não definido.');
            return;
        }
        if (texto.trim() === '' && files.length === 0) {
            setError('Por favor, adicione um texto ou um arquivo.');
            return;
        }

        let input: PostInput;

        if (usarIdentidades && identidadeSelecionada) {
            const ids = resolverIdsParaMutacao(identidadeSelecionada);
            input = {
                texto,
                autorAtletaId: ids.autorAtletaId || undefined,
                autorTorneioId: ids.autorTorneioId || undefined,
                autorLigaId: ids.autorLigaId || undefined,
            };
        } else if (ligaId) {
            input = { texto, autorLigaId: ligaId };
        } else if (isPostingAsLiga && autorLiga) {
            input = { texto, autorLigaId: autorLiga.id };
        } else if (autorAtleta) {
            input = { texto, autorAtletaId: autorAtleta.id };
        } else {
            input = { texto, autorTorneioId: autorTorneio!.id };
        }

        try {
            await criarPost(input, files);
            setTexto('');
            setFiles([]);
            setError(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setSnackbar({ open: true, message: 'Post publicado com sucesso!', severity: 'success' });
            onPostCreated?.();
        } catch (err) {
            console.error(err);
            setError('Erro ao criar post.');
            setSnackbar({ open: true, message: 'Erro ao criar post.', severity: 'error' });
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    return (
        <div className="card w-100 shadow-xss rounded-xxl border-0 ps-4 pt-4 pe-4 pb-3 mb-3">
            <div className="card-body p-0">
                <div className="font-xssss fw-600 text-grey-500 card-body p-0 d-flex align-items-center">
                    <i className="btn-round-sm font-xs text-primary bi bi-pencil me-2 bg-greylight"></i>
                    Nova Publicação
                </div>
            </div>

            {usarIdentidades && identidadeSelecionada && (
                <div className="card-body p-0 mt-2">
                    <IdentidadeFeedSelector
                        identidades={identidades!}
                        identidadeSelecionada={identidadeSelecionada}
                        onSelecionar={setIdentidadeSelecionada}
                    />
                </div>
            )}

            {hasMultipleIdentities && (
                <div className="card-body p-0 mt-2">
                    <div className="d-flex align-items-center gap-2">
                        <span className="font-xssss fw-600 text-grey-500">Publicando como:</span>
                        <div className="dropdown">
                            <button
                                className="btn btn-light btn-sm dropdown-toggle d-flex align-items-center gap-2"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <img
                                    src={selectedAutor === 'liga'
                                        ? (autorLiga?.imagemUrl || '/assets/images/avatar.jpg')
                                        : (autorTorneio?.avatarUrl || '/assets/images/avatar.jpg')}
                                    alt=""
                                    className="rounded-circle"
                                    style={{ width: 24, height: 24, objectFit: 'cover' }}
                                />
                                <span className="font-xssss fw-600">
                                    {selectedAutor === 'liga' ? autorLiga?.nome : autorTorneio?.nome}
                                </span>
                            </button>
                            <ul className="dropdown-menu">
                                <li>
                                    <button
                                        className={`dropdown-item d-flex align-items-center gap-2 ${selectedAutor === 'liga' ? 'active' : ''}`}
                                        type="button"
                                        onClick={() => setSelectedAutor('liga')}
                                    >
                                        <img
                                            src={autorLiga?.imagemUrl || '/assets/images/avatar.jpg'}
                                            alt=""
                                            className="rounded-circle"
                                            style={{ width: 24, height: 24, objectFit: 'cover' }}
                                        />
                                        <span className="font-xssss fw-600">{autorLiga?.nome}</span>
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className={`dropdown-item d-flex align-items-center gap-2 ${selectedAutor === 'torneio' ? 'active' : ''}`}
                                        type="button"
                                        onClick={() => setSelectedAutor('torneio')}
                                    >
                                        <img
                                            src={autorTorneio?.avatarUrl || '/assets/images/avatar.jpg'}
                                            alt=""
                                            className="rounded-circle"
                                            style={{ width: 24, height: 24, objectFit: 'cover' }}
                                        />
                                        <span className="font-xssss fw-600">{autorTorneio?.nome}</span>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            <div className="card-body p-0 mt-3 position-relative">
                <textarea
                    name="message"
                    className="h100 bor-0 w-100 rounded-xxl p-2 ps-3 font-xssss text-grey-500 fw-500 border-light-md theme-dark-bg"
                    cols={30}
                    rows={10}
                    placeholder="Conte algo sobre seu treino, último play ou qualquer outro assunto ;D"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    style={{ resize: 'none' }}
                ></textarea>
            </div>

            {files.length > 0 && (
                <div className="card-body p-0 mt-2">
                    <div className="d-flex flex-wrap gap-2">
                        {files.map((file, index) => {
                            const url = previewUrls[index];
                            const large = file.size > 1024 * 1024 * 10; // 10MB
                            return (
                                <div key={index} className="position-relative">
                                    {file.type.startsWith('image/') && !large ? (
                                        <img
                                            src={url}
                                            alt={file.name}
                                            className="rounded"
                                            style={{ width: 100, height: 100, objectFit: 'cover' }}
                                            onError={(e) => {
                                                const target = e.currentTarget;
                                                target.style.display = 'none';
                                            }}
                                        />
                                    ) : file.type.startsWith('video/') && !large ? (
                                        <video
                                            src={url}
                                            controls
                                            className="rounded"
                                            style={{ width: 150, height: 100, objectFit: 'cover' }}
                                            onError={(e) => {
                                                const target = e.currentTarget;
                                                target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center bg-light border rounded" style={{ width: 150, height: 100 }}>
                                            <i className="bi bi-file me-2"></i>
                                            <span className="font-xssss text-truncate" style={{ maxWidth: 100 }}>{file.name}</span>
                                        </div>
                                    )}
                                    {large && (
                                        <div className="small text-muted">Arquivo grande para preview</div>
                                    )}
                                    <button
                                        type="button"
                                        className="btn-close position-absolute top-0 end-0"
                                        aria-label="Remover"
                                        onClick={() => handleRemoveFile(index)}
                                    ></button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger mt-2" role="alert">
                    {error}
                </div>
            )}

            <div className="card-body d-flex p-0 mt-0">
                {/* A liga também anexa foto/vídeo: `PostService.criar` grava MidiaPost
                    para qualquer tipo de autor — o bloqueio era só do frontend (C-03). */}
                <a
                    href="#photo"
                    onClick={(e) => {
                        e.preventDefault();
                        fileInputRef.current?.click();
                    }}
                    className="d-flex align-items-center font-xssss ls-1 text-grey-700 text-dark px-2 py-1 btn-post-upload"
                >
                    <i className="font-md text-primary bi bi-image me-2 fs-3"></i>
                    <span className="d-none-xs fw-700 fs-5">Foto/Vídeo</span>
                </a>
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="d-none"
                />

                <button
                    type="button"
                    id='publicarButton'
                    className="ms-auto btn btn-primary rounded-l fw-500 "
                    style={{border: 'none'}}
                    onClick={handlePublish}
                    disabled={!canPublish}
                >
                    PUBLICAR
                </button>

                <div
                    className={`dropdown-menu p-4 right-0 rounded-xxl border-0 shadow-lg ${menuClass}`}
                    aria-labelledby="dropdownMenu4"
                >
                    <DropdownItem icon="bi bi-bookmark" title="Save Link" desc="Add this to your saved items" />
                    <DropdownItem icon="bi bi-exclamation-circle" title="Hide Post" desc="Save to your saved items" />
                    <DropdownItem icon="bi bi-exclamation-octagon" title="Hide all from Group" desc="Save to your saved items" />
                    <DropdownItem icon="bi bi-lock" title="Unfollow Group" desc="Save to your saved items" />
                </div>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

interface DropdownItemProps {
    icon: string;
    title: string;
    desc: string;
}

const DropdownItem: React.FC<DropdownItemProps> = ({ icon, title, desc }) => (
    <div className="card-body p-0 d-flex mt-2">
        <i className={`${icon} text-grey-500 me-3 font-lg`}></i>
        <h4 className="fw-600 text-grey-900 font-xssss mt-0 me-4 pointer">
            {title}
            <span className="d-block font-xsssss fw-500 mt-1 lh-3 text-grey-500">{desc}</span>
        </h4>
    </div>
);

export default Createpost;
